<template>
    <DCard>
        <section class="products-table">
            <div class="search">
                <DRow
                    class="p-2"
                    align-vertical="end"
                >
                    <DCol
                        sm="12"
                        class="text-right"
                    >
                        <DButton
                            v-if="userPermissions.includes('product.view')"
                            id="price-plan-coverage-view"
                            icon="eye"
                            :label="__('products.products.index.addon_products_price_plan_coverage_view')"
                            :href="pricePlanCoverageLink()"
                        />
                    </DCol>
                </DRow>
                <DRow>
                    <DCol sm="2">
                        <h6>
                            {{ __('messages.search') }}
                        </h6>
                    </DCol>
                    <DCol sm="2">
                        <h6>
                            {{ __('products.products.filter.fulfillment_modules') }}
                        </h6>
                    </DCol>
                    <DCol sm="2">
                        <h6>
                            {{ __('products.products.filter.brands') }}
                        </h6>
                    </DCol>
                    <DCol sm="2">
                        <h6>
                            {{ __('products.products.index.table.headers.classes') }}
                        </h6>
                    </DCol>
                    <DCol sm="2">
                        <h6>
                            {{ __('products.products.filter.retirement_status') }}
                        </h6>
                    </DCol>
                </DRow>
                <DRow>
                    <DCol sm="2">
                        <DInput
                            id="search"
                            v-model="filter.search"
                            :placeholder="__('products.products.filter.search')"
                            @keydown.enter.exact.prevent="submitFilters"
                        />
                    </DCol>
                    <DCol sm="2">
                        <DMultiSelect
                            id="ff-modules-dropdown"
                            :options="moduleOptions"
                            :search="true"
                            :selection="true"
                            :selected="selectedModules"
                            :options-empty-placeholder="__('products.productGroups.form.no_options_found')"
                            selection-type="text"
                            @update="updateSelectedModules"
                        />
                    </DCol>
                    <DCol sm="2">
                        <DMultiSelect
                            id="brands-dropdown"
                            :options="brandOptions"
                            :search="true"
                            :selection="true"
                            :selected="selectedBrands"
                            :options-empty-placeholder="__('products.productGroups.form.no_options_found')"
                            selection-type="text"
                            @update="updateSelectedBrands"
                        />
                    </DCol>
                    <DCol sm="2">
                        <DMultiSelect
                            id="classes-dropdown"
                            :options="classOptions"
                            :search="true"
                            :selection="true"
                            :selected="selectedClass"
                            :options-empty-placeholder="__('products.productGroups.form.no_options_found')"
                            selection-type="text"
                            @update="updateSelectedClasses"
                        />
                    </DCol>
                    <DCol sm="2">
                        <DSwitch
                            id="retirement"
                            style="margin-left: 10px;"
                            color="success"
                            variant="3d"
                            :checked="shouldIncludeRetired"
                            @update:checked="updateRetired"
                        />
                    </DCol>
                    <DCol sm="2">
                        <DButton
                            id="filter-apply"
                            :label="__('messages.actions.filter')"
                            @click="submitFilters"
                        />
                        <DButton
                            id="filter-reset"
                            :label="__('messages.actions.reset_filter')"
                            @click="resetFilters"
                        />
                    </DCol>
                </DRow>
            </div>
            <div class="table-header">
                <div>
                    <DButton
                        mini
                        :icon="collapsedGroups.length > 0 ? 'arrow-down' : 'arrow-up'"
                        @click="toggleAll()"
                    />
                </div>
                <div>{{ __('products.products.index.table.headers.id') }}</div>
                <div>{{ __('products.products.index.table.headers.name') }}</div>
                <div>{{ __('products.products.index.table.headers.classes') }}</div>
                <div />
            </div>
            <main>
                <div
                    v-for="(item, index) in productGroups"
                    :key="index"
                    class="item-group"
                >
                    <div class="item-header">
                        <div>
                            <DButton
                                :id="`toggle_group_${index}`"
                                mini
                                :icon="collapsedGroups.includes(index) ? 'arrow-down' : 'arrow-up'"
                                @click="toggleGroup(index)"
                            />
                        </div>
                        <div>
                            {{ item.name }}
                            <DBadge
                                v-if="item.retired"
                                color="info"
                            >
                                {{ __('products.products.index.retired') }}
                            </DBadge>
                        </div>
                        <div style="display: flex; grid-gap: 10px;">
                            <span
                                v-if="userPermissions.includes('product.view') || userPermissions.includes('contract.edit')"
                                @click="isInLoadingState ? '' : updateProductGroupFavorite(item.id, item.isFavorite)"
                            >
                                <DIcon
                                    :name="item.isFavorite === null ? 'star-outline' : 'star'"
                                    :size="32"
                                    role="button"
                                    :class="item.isFavorite === null ? '' : 'favorite'"
                                    :disabled="isInLoadingState"
                                />
                            </span>
                            <DButton
                                v-if="userPermissions.includes('product.edit')"
                                :id="`edit_group_${item.id}`"
                                icon="pencil"
                                mini
                                :href="editProductGroup(item.id)"
                            />
                            <DButton
                                v-if="userPermissions.includes('product.delete')"
                                :id="`delete_group_${item.id}`"
                                :disabled="item.products.length > 0 || isInLoadingState"
                                icon="trash-can-outline"
                                color="danger"
                                mini
                                @click="deleteProductGroup(item.id)"
                            />
                        </div>
                    </div>
                    <div
                        v-for="(product, indexProduct) in item.products"
                        v-show="!collapsedGroups.includes(index)"
                        :key="indexProduct"
                        class="item"
                    >
                        <div />
                        <div>{{ product.id }}</div>
                        <div>
                            {{ product.name }}
                            <DBadge
                                v-if="item.retired || product.retired"
                                color="info"
                            >
                                {{ __('products.products.index.retired') }}
                            </DBadge>
                        </div>
                        <div>
                            {{ product.classes.length > 0 ? parseProductClasses(product.classes) : '' }}
                        </div>
                        <div style="display: flex; grid-gap: 10px">
                            <DButton
                                v-if="userPermissions.includes('product.view')"
                                :id="`view_product_${product.id}`"
                                icon="eye"
                                mini
                                :href="viewProductLink(product.id)"
                            />
                            <DButton
                                v-if="userPermissions.includes('product.edit')"
                                :id="`edit_product_${product.id}`"
                                icon="pencil"
                                mini
                                :href="editProductLink(product.id)"
                            />
                            <DButton
                                v-if="userPermissions.includes('product.delete')"
                                :id="`delete_product_${product.id}`"
                                :disabled="product.has_contracts === true || isInLoadingState"
                                icon="trash-can-outline"
                                color="danger"
                                mini
                                @click="deleteProduct(product.id)"
                            />
                        </div>
                    </div>
                </div>
            </main>
        </section>

        <DFabs>
            <DFab
                v-if="userPermissions.includes('product.view')"
                id="download-upload"
                icon="download"
                :label="__('products.products.index.download_upload')"
                @click="$inertia.get($route('products.product-addons.download-upload'))"
            />
            <DFab
                v-if="userPermissions.includes('product.create')"
                id="btn-add"
                icon="plus"
                :label="__('products.products.index.addProductAddonButton')"
                @click="$inertia.get($route('products.product-addons.create'))"
            />
            <DFab
                v-if="userPermissions.includes('product.create')"
                id="btn-add-product-group"
                icon="plus"
                color="secondary"
                :label="__('products.products.index.addProductAddonGroupButton')"
                @click="$inertia.get($route('products.product-addon-groups.create'))"
            />
        </DFabs>

        <DModal
            v-model:show="showConfirmationModal"
            :title="__('products.products.index.deleteConfirmation.title')"
            :message="__('products.products.index.deleteConfirmation.message')"
            is-confirm
            is-error
            @close="showConfirmationModal = false"
            @confirm="deleteCallBack"
        />

        <DModal
            v-model:show="showFavoriteModal"
            :title="favoriteTitle"
            :message="favoriteMessage"
            is-confirm
            @close="showFavoriteModal = false"
            @confirm="postCallBack()"
        />

        <DModal
            v-model="activity_log_comment"
            :show="showDeleteProductConfirmationModal || modalHasErrors"
            :title="__('products.products.index.deleteProductAddonConfirmation.title')"
            :message="__('products.products.index.deleteProductAddonConfirmation.message')"
            is-confirm
            is-error
            :is-awaiting-validation="true"
            :show-change-reason-input="true"
            @close="showConfirmationModal = false"
            @confirm="deleteCallBack"
        />
    </DCard>
</template>

<script>
export default {
    name: 'ProductAddonsIndex',
    props: {
        productGroups: {
            type: Array,
            default () {
                return []
            }
        },
        brands: {
            type: Array,
            default () {
                return []
            }
        },
        modules: {
            type: Array,
            default () {
                return []
            }
        },
        classes: {
            type: Array,
            default () {
                return []
            }
        },
        filterData: {
            type: Object,
            default () {
                return {}
            }
        }
    },
    data () {
        return {
            showConfirmationModal: false,
            showDeleteProductConfirmationModal: false,
            deleteCallBack: () => { return {} },
            collapsedGroups: [],
            filter: {
                search: (this.filterData !== null && this.filterData.search) || this.getParam('search'),
                brands: (this.filterData !== null && this.filterData.brands) || this.getParamArray('brands'),
                modules: (this.filterData !== null && this.filterData.modules) || this.getParamArray('modules'),
                retired: (this.filterData !== null && this.filterData.retired) || this.getParam('retired'),
                classes: (this.filterData !== null && this.filterData.classes) || this.getParam('classes')
            },
            showFavoriteModal: false,
            postCallBack: () => { return {} },
            favoriteTitle: '',
            favoriteMessage: '',
            activity_log_comment: null,
            modalHasErrors: false
        }
    },
    computed: {
        userPermissions () {
            return this.$page.props.auth.permissions
        },
        moduleOptions () {
            return this.modules.map((value) => {
                return { value, text: value, selected: this.filterData?.modules?.includes(String(value)) }
            })
        },
        brandOptions () {
            return this.brands.map((item) => {
                return { value: item.value, text: item.label, selected: this.filterData?.brands?.includes(String(item.value)) }
            })
        },
        classOptions () {
            return this.classes.map((item) => {
                return { value: item.value, text: item.label, selected: this.filterData?.classes?.includes(String(item.value)) }
            })
        },
        selectedModules () {
            if (!this.filter.modules) return []

            return this.filter.modules.map((item) => item)
        },
        selectedBrands () {
            if (!this.filter.brands) return []

            return this.filter.brands.map((item) => parseInt(item))
        },
        selectedClass () {
            if (!this.filter.classes) return []

            return this.filter.classes.map((item) => parseInt(item))
        },
        shouldIncludeRetired () {
            return Number(this.filter.retired) !== 0
        }
    },
    methods: {
        editProductGroup (id) {
            return this.$route('products.product-addon-groups.edit', { product_group: id })
        },
        viewProductLink (id) {
            return this.$route('products.product-addons.show', id)
        },
        editProductLink (id) {
            return this.$route('products.product-addons.edit', { product: id })
        },
        deleteProductGroup (id) {
            this.deleteCallBack = () => {
                this.$inertia.delete(this.$route('products.product-addon-groups.destroy', { product_group: id }))
                this.showConfirmationModal = false
            }
            this.showConfirmationModal = true
        },
        deleteProduct (id) {
            this.deleteCallBack = () => {
                this.$inertia.delete(this.$route('products.product-addons.destroy', {
                    product: id,
                    activity_log_comment: this.activity_log_comment
                }), {
                    onSuccess: () => {
                        this.showDeleteProductConfirmationModal = false
                        this.modalHasErrors = false
                    },
                    onError: () => {
                        this.modalHasErrors = true
                    }
                })
            }
            this.showDeleteProductConfirmationModal = true
        },
        updateProductGroupFavorite (id, isFavorite) {
            this.postCallBack = () => {
                this.$inertia.post(
                    this.$route('products.product-addon-groups.update-favorite', { product_group: id }),
                    { favorite: isFavorite === null },
                    {}
                )
                this.showFavoriteModal = false
            }

            this.favoriteTitle = isFavorite === null
                ? this.__('products.productGroups.addFavorite.title')
                : this.__('products.productGroups.removeFavorite.title')
            this.favoriteMessage = isFavorite === null
                ? this.__('products.productGroups.addFavorite.message')
                : this.__('products.productGroups.removeFavorite.message')
            this.showFavoriteModal = true
        },
        toggleGroup (index) {
            if (this.collapsedGroups.includes(index)) {
                this.collapsedGroups = this.collapsedGroups.filter(e => e !== index)
            } else {
                this.collapsedGroups.push(index)
            }
        },
        toggleAll () {
            if (this.collapsedGroups.length > 0) {
                this.collapsedGroups = []
            } else {
                this.productGroups.forEach((productGroup, index) => {
                    if (!this.collapsedGroups.includes(index)) {
                        this.collapsedGroups.push(index)
                    }
                })
            }
        },
        getParamArray (paramName) {
            const params = new URLSearchParams(window.location.search)
            const paramArray = []
            const expression = new RegExp('^' + paramName + '[\\[]\\d*[\\]]$')
            for (const param of params.entries()) {
                if (param[0].match(expression)) {
                    paramArray.push(param[1])
                }
            }
            return paramArray
        },
        getParam (paramName) {
            const params = new URLSearchParams(window.location.search)
            return params.has(paramName) ? params.get(paramName) : null
        },
        updateSelectedModules (values) {
            this.filter.modules = values
        },
        updateSelectedBrands (values) {
            this.filter.brands = values
        },
        updateSelectedClasses (values) {
            this.filter.classes = values
        },
        updateRetired (values) {
            this.filter.retired = values ? 1 : 0
        },
        submitFilters () {
            this.$inertia.get(this.$route('products.product-addons.index', {
                _query: this.filter,
                saveFilters: true
            }))
        },
        resetFilters () {
            this.$inertia.get(this.$route('products.product-addons.index', {
                saveFilters: true,
                defaultFilters: true
            }))
        },
        parseProductClasses (classes) {
            const names = classes.map((item) => item.name)

            return names.join(', ')
        },
        pricePlanCoverageLink () {
            return this.$route('products.products.addon-products-price-plan-coverage')
        },
    }
}
</script>

<style scoped lang="scss">
.products-table {
    & .table-header {
        border-bottom: 1px solid rgba(0,0,0,0.14);
        display: grid;
        grid-template-columns: 45px 100px 1fr 400px 160px;
        grid-gap: 10px;
        align-items: center;

        & > div {
            padding: 10px;
            font-weight: bold;
        }
    }
    & main {
        & .item-group {
            border-bottom: 1px solid rgba(0,0,0,0.3);
            position: relative;

            & .item-header {
                display: grid;
                grid-template-columns: 45px 100px 1fr 160px;
                grid-gap: 10px;
                background: #fff;
                border-bottom: 1px solid rgba(0,0,0,0.14);
                position: sticky;
                top: -25px;
                width: 100%;
                z-index: 100;

                & > div {
                    padding: 10px;
                    align-self: center;
                }
                & > div:nth-child(2) {
                    grid-column: 2 / span 2;
                    font-weight: bold;
                }
            }
            & .item {
                display: grid;
                grid-template-columns: 45px 100px 1fr 400px 160px;
                grid-gap: 10px;

                & > div {
                    padding: 10px;
                    align-self: center;
                }
                & > div:nth-child(1) {
                    position: relative;
                    overflow: hidden;
                    padding: 0;
                    height: 100%;

                    &::after {
                        content: "";
                        display: block;
                        position: absolute;
                        left: 29px;
                        top: 0px;
                        width: 2px;
                        height: 60px;
                        background: rgba(0,0,0,0.08);
                    }
                    &::before {
                        content: "";
                        display: block;
                        position: absolute;
                        left: 31px;
                        top: 25px;
                        width: 10px;
                        border-top-right-radius: 4px;
                        border-bottom-right-radius: 4px;
                        height: 2px;
                        background: rgba(0,0,0,0.08);
                    }
                }
                &:last-of-type > div:nth-child(1)::after {
                    height: 35px;
                    background: rgba(0,0,0,0.07);
                    border-bottom-left-radius: 4px;
                    border-bottom-right-radius: 4px;
                }

                &:hover {
                    background: rgba(0,0,0,0.07);
                }
            }
        }
    }
}
.search {
    padding: 7px 7px 7px 14px;
    border-bottom: var(--colorAppBorder) solid 1px;
}
.favorite {
    color: var(--colorPrimary);
}
</style>
